import { Directions, Phone } from "@mui/icons-material";
import WatchIcon from "@mui/icons-material/QueryBuilder";
import BuyOnline from "@mui/icons-material/ShoppingCart";
import { Button, CircularProgress, Stack, useMediaQuery } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";
import {
  getBuyOnlineUrl,
  getDeliveryEntitiesForStores,
} from "../../Services/Data.service";
import { getChainBuyUrlOverride, mapsSelector } from "../../Utils/Utils";

function OpenInfo({ item }) {
  const isSmall = useMediaQuery("(max-width: 360px)");

  const onHoursClick = (e) => {
    e.stopPropagation();
    window.open(
      "https://www.google.com/maps/search/?api=1&query=" +
        encodeURI(
          [item.chain, item.address].filter((c) => !!c).join(" ")
        ).replaceAll("&", "")
    );
  };

  return (
    <>
      {item.loading_opening_hours && (
        <CircularProgress
          style={{ marginTop: "10px" }}
          size={24}
          color={"primary"}
        />
      )}
      {!item.loading_opening_hours && typeof item.open_now === "boolean" && (
        <Button
          color={item.open_now ? "success" : "error"}
          startIcon={<WatchIcon />}
          size="small"
          sx={{
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          {isSmall ? "" : item.open_now ? "Open" : "Closed"}
        </Button>
      )}
      {!item.loading_opening_hours && typeof item.open_now !== "boolean" && (
        <Button
          color="inherit"
          startIcon={<WatchIcon />}
          onClick={onHoursClick}
          size="small"
          sx={{
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          Hours
        </Button>
      )}
    </>
  );
}

function BuyOnlineLink({ item, deliveryEntities, direction, filters }) {
  const [linkBuy, setLinkBuy] = useState(null);
  const isSmall = useMediaQuery("(max-width: 360px)") && direction !== "column";
  const config = useContext(UserContext)?.state?.config;

  useEffect(() => {
    const override = getChainBuyUrlOverride(config, item);
    if (override) {
      setLinkBuy(override);
      return;
    }
    const product_id = filters?.product?.[0];

    getBuyOnlineUrl(item.id, product_id)
      .then((response) => {
        setLinkBuy(response?.buy_online_url || item.link_buy);
      })
      .catch(() => {
        setLinkBuy(item.link_buy);
      });
  }, [filters, item, config]);

  return (
    <>
      {linkBuy && (
        <Button
          className="store-buy-online-btn"
          id={`${item.id}`}
          startIcon={<BuyOnline />}
          onClick={(e) => {
            e.stopPropagation();
            window.open(linkBuy);
          }}
          sx={{
            color: "#0299F7",
            display: "flex",
            alignItems: "center",
            flexDirection: direction,
            fontSize: "0.7rem",
            textTransform: "none",
            "& .MuiButton-icon": {
              marginRight: 0,
            },
          }}
          size="small"
        >
          {isSmall ? "" : "Shop"}
        </Button>
      )}
    </>
  );
}

function DirectionsButton({ item, direction }) {
  const isSmall = useMediaQuery("(max-width: 360px)") && direction !== "column";
  return (
    <Button
      startIcon={<Directions />}
      color="inherit"
      size="small"
      sx={{
        color: "black",
        display: "flex",
        alignItems: "center",
        flexDirection: direction,
        fontSize: "0.7rem",
        textTransform: "none",
        "& .MuiButton-icon": {
          marginRight: 0,
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        mapsSelector(item.latitude, item.longitude);
      }}
    >
      {isSmall ? "" : "Directions"}
    </Button>
  );
}
function ContactInfo({ item, deliveryEntities, direction }) {
  const { deliveryInventory } = useContext(MapContext);
  const storeInventory = deliveryInventory?.filter((i) =>
    (deliveryEntities || [])
      .map((de) => de.delivery_entity_uid)
      .includes(i.delivery_entity_uid)
  );
  const deliveryEntityPhone = storeInventory?.find(
    (i) => !!i?.delivery_entity?.phone
  )?.delivery_entity?.phone;
  const phone = item.phone || deliveryEntityPhone;
  return (
    <>
      {phone && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            window.open(`tel:${phone}`);
          }}
          color="inherit"
          startIcon={<Phone />}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: direction,
            fontSize: "0.7rem",
            textTransform: "none",
            "& .MuiButton-icon": {
              marginRight: 0,
            },
          }}
        >
          Call
        </Button>
      )}
    </>
  );
}
const StoreActionButtons = ({
  filters,
  item,
  direction = "column",
  show = ["buyOnlineLink", "openInfo", "directionsButton", "callButton"],
  containerProps = {},
}) => {
  const userContext = useContext(UserContext);
  const storeLocatorConfig = userContext?.state?.config;
  const [deliveryEntities, setDeliveryEntities] = useState([]);

  const fetchDeliveryEntities = useCallback(() => {
    if (item?.delivery_entities?.length) {
      setDeliveryEntities(item.delivery_entities);
    } else {
      getDeliveryEntitiesForStores([item?.id]).then((response) => {
        setDeliveryEntities(response || []);
      });
    }
  }, [item?.id]);

  useEffect(() => {
    if (item?.id) {
      fetchDeliveryEntities();
    } else {
      setDeliveryEntities([]);
    }
  }, [item?.id]);

  const getAction = (action) => {
    switch (action) {
      case "buyOnlineLink":
        return !!storeLocatorConfig?.storeDetails?.buyOnlineLink ? (
          <BuyOnlineLink
            item={item}
            deliveryEntities={deliveryEntities}
            direction={direction === "row" ? "column" : "row"}
            filters={filters}
          />
        ) : (
          <></>
        );
      case "openInfo":
        return !!storeLocatorConfig?.storeDetails?.openInfo ? (
          <OpenInfo
            item={item}
            direction={direction === "row" ? "column" : "row"}
          />
        ) : (
          <></>
        );
      case "directionsButton":
        return !!storeLocatorConfig?.storeDetails?.directionsButton ? (
          <DirectionsButton
            item={item}
            direction={direction === "row" ? "column" : "row"}
          />
        ) : (
          <></>
        );
      case "callButton":
        return !!storeLocatorConfig?.storeDetails?.contactInfo ? (
          <ContactInfo
            item={item}
            deliveryEntities={deliveryEntities}
            direction={direction === "row" ? "column" : "row"}
          />
        ) : (
          <></>
        );
      default:
        return <></>;
    }
  };

  return (
    <Stack
      direction={direction}
      alignItems={direction === "row" ? "flex-end" : "flex-start"}
      fontSize={12}
      {...containerProps}
    >
      {show.map((action) => getAction(action))}
    </Stack>
  );
};

export default StoreActionButtons;
